/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Footer } from "@/components/landing/footer";
import MainNavigation from "@/components/layout/main-navigation";
import { Suspense, lazy } from "react";
import type { SolutionTemplateProps } from "../solution-template.types";
import { SolutionHeroSection } from "./solution-hero-section";

const TrustBarSection = lazy(() =>
  import("./trust-bar-section").then((m) => ({ default: m.TrustBarSection })),
);
const PainPointsSection = lazy(() =>
  import("./pain-points-section").then((m) => ({
    default: m.PainPointsSection,
  })),
);
const StatsBarSection = lazy(() =>
  import("./stats-bar-section").then((m) => ({ default: m.StatsBarSection })),
);
const HowItWorksSection = lazy(() =>
  import("./how-it-works-section").then((m) => ({
    default: m.HowItWorksSection,
  })),
);
const SolutionFeaturesSection = lazy(() =>
  import("./solution-features-section").then((m) => ({
    default: m.SolutionFeaturesSection,
  })),
);
const BeforeAfterSection = lazy(() =>
  import("./before-after-section").then((m) => ({
    default: m.BeforeAfterSection,
  })),
);
const SolutionROISection = lazy(() =>
  import("./solution-roi-section").then((m) => ({
    default: m.SolutionROISection,
  })),
);
const SolutionFAQSection = lazy(() =>
  import("./solution-faq-section").then((m) => ({
    default: m.SolutionFAQSection,
  })),
);
const RelatedResourcesSection = lazy(() =>
  import("./related-resources-section").then((m) => ({
    default: m.RelatedResourcesSection,
  })),
);
const SolutionCTASection = lazy(() =>
  import("./solution-cta-section").then((m) => ({
    default: m.SolutionCTASection,
  })),
);

const SectionSkeleton = () => (
  <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 py-8 md:py-16 lg:py-20">
    <div className="h-48 animate-pulse rounded-lg bg-muted" />
  </div>
);

export function SolutionTemplate({ config }: SolutionTemplateProps) {
  return (
    <div className="min-h-screen bg-background-warm">
      <MainNavigation />

      <SolutionHeroSection hero={config.hero} />

      <Suspense fallback={<SectionSkeleton />}>
        <TrustBarSection trustBar={config.trustBar} />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <PainPointsSection
          title={config.painPoints.title}
          description={config.painPoints.description}
          items={config.painPoints.items}
        />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <StatsBarSection stats={config.stats} />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <HowItWorksSection
          title={config.howItWorks.title}
          description={config.howItWorks.description}
          steps={config.howItWorks.steps}
        />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <SolutionFeaturesSection
          title={config.features.title}
          description={config.features.description}
          items={config.features.items}
        />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <BeforeAfterSection beforeAfter={config.beforeAfter} />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <SolutionROISection roi={config.roi} />
      </Suspense>

      <Suspense fallback={<SectionSkeleton />}>
        <SolutionFAQSection title={config.faq.title} items={config.faq.items} />
      </Suspense>

      {config.tradeSlug && (
        <Suspense fallback={<SectionSkeleton />}>
          <RelatedResourcesSection
            tradeName={config.tradeName}
            tradeSlug={config.tradeSlug}
          />
        </Suspense>
      )}

      <Suspense fallback={<SectionSkeleton />}>
        <SolutionCTASection cta={config.cta} />
      </Suspense>

      <Footer />
    </div>
  );
}

export type {
  SolutionCTA,
  SolutionFeature,
  SolutionHero,
  SolutionROI,
  SolutionTemplateConfig,
  SolutionTemplateProps,
} from "../solution-template.types";
